const FRAME_ID = 'header-booking-iframe';

let resizerPromise = null;

/**
 * Loads iframe-resizer once per page.
 *
 * @param {string} src - URL of the iframe-resizer bundle.
 * @returns {Promise<void>}
 */
function loadResizer(src) {
  if (typeof window.iFrameResize === 'function') return Promise.resolve();
  if (resizerPromise) return resizerPromise;

  resizerPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.addEventListener('load', () => resolve());
    script.addEventListener('error', () => {
      resizerPromise = null;
      reject(new Error('Could not load iframe-resizer'));
    });
    document.head.appendChild(script);
  });

  return resizerPromise;
}

/**
 * Builds the booking iframe and starts iframe-resizer on it.
 *
 * @param {HTMLElement} drawer - The <dialog-component> holding the booking drawer.
 * @returns {Promise<void>}
 */
async function mountBookingFrame(drawer) {
  const mount = drawer.querySelector('[data-booking-mount]');
  if (!mount || mount.querySelector('iframe')) return;

  const src = drawer.dataset.bookingSrc;
  const origin = drawer.dataset.bookingOrigin;
  const resizerSrc = drawer.dataset.bookingResizer;
  if (!src || !resizerSrc) return;

  const iframe = document.createElement('iframe');
  // The booking page ships its own ids, so this one is namespaced to stay unique
  // if the drawer is opened on /pages/book-tid-butik, which embeds the same widget.
  iframe.id = FRAME_ID;
  iframe.className = 'booking-drawer__iframe';
  iframe.title = drawer.querySelector('.cart-drawer__heading')?.textContent.trim() || 'Booking';
  iframe.setAttribute('scrolling', 'no');
  iframe.src = src;

  iframe.addEventListener(
    'load',
    () => {
      mount.setAttribute('data-booking-ready', 'true');
    },
    { once: true }
  );

  mount.appendChild(iframe);

  try {
    await loadResizer(resizerSrc);
  } catch (error) {
    // Without the resizer the iframe still works, it just keeps the fallback height.
    mount.setAttribute('data-booking-ready', 'true');
    return;
  }

  window.iFrameResize(
    {
      log: false,
      checkOrigin: origin ? [origin] : false,
      // The booking page uses 'max', which takes the largest of every measurement method.
      // In a narrow drawer the widget's expanding panels reflow as the height changes, so the
      // methods disagree and 'max' flips between them: measured flapping between 426px and
      // 626px. 'lowestElement' measures the actual bottom-most element and handles the
      // absolutely positioned dropdowns this widget uses.
      heightCalculationMethod: 'lowestElement',
      // Ignore sub-pixel churn so a settled layout stops resizing.
      tolerance: 4,
      scrolling: false,
    },
    `#${FRAME_ID}`
  );
}

/**
 * Keeps focus on the close button after opening.
 *
 * The booking widget is cross-origin: once focus moves inside it, key events never reach this
 * document and Escape stops closing the drawer. Holding focus on the close button keeps Escape,
 * and the tab order, working until the visitor deliberately clicks into the widget.
 *
 * @param {HTMLElement} drawer - The <dialog-component> holding the booking drawer.
 */
function holdFocus(drawer) {
  const closeButton = drawer.querySelector('[data-booking-close]');
  const frame = drawer.querySelector('[data-booking-mount]');
  if (!(closeButton instanceof HTMLElement)) return;

  // Once the visitor reaches for the widget we stop interfering; from then on the widget owns
  // the keyboard and the close button or the overlay are the way out.
  //
  // A click inside a cross-origin iframe raises no event in this document, so the pointer
  // entering the iframe's box is the only signal available. Without it every click in the
  // widget would be answered by yanking focus back here, which breaks the widget's own
  // handling.
  let visitorEnteredWidget = false;
  let reclaimed = false;

  // The only focus grab worth undoing is the one the widget performs while booting. Reclaiming
  // is therefore allowed once, and only during the moments right after opening: fighting the
  // visitor for focus is far worse than Escape going unanswered.
  const reclaimWindowClosesAt = Date.now() + 5000;

  const markEntered = () => {
    visitorEnteredWidget = true;
  };

  const onPointerDown = () => {
    visitorEnteredWidget = true;
  };

  const onBlur = () => {
    if (visitorEnteredWidget || reclaimed || Date.now() > reclaimWindowClosesAt) return;

    // window blur fires as the iframe takes focus; the active element is only updated after.
    setTimeout(() => {
      if (visitorEnteredWidget || reclaimed) return;
      const active = document.activeElement;
      if (active instanceof HTMLIFrameElement && drawer.contains(active)) {
        reclaimed = true;
        closeButton.focus();
      }
    });
  };

  closeButton.focus();
  drawer.addEventListener('pointerdown', onPointerDown, true);
  frame?.addEventListener('pointerenter', markEntered);
  frame?.addEventListener('touchstart', markEntered, { passive: true });
  window.addEventListener('blur', onBlur);

  drawer.addEventListener(
    'dialog:close',
    () => {
      drawer.removeEventListener('pointerdown', onPointerDown, true);
      frame?.removeEventListener('pointerenter', markEntered);
      frame?.removeEventListener('touchstart', markEntered);
      window.removeEventListener('blur', onBlur);
    },
    { once: true }
  );
}

function init() {
  const drawer = document.querySelector('[data-booking-drawer]');
  if (!drawer) return;

  drawer.addEventListener('dialog:open', () => {
    void mountBookingFrame(drawer);
    holdFocus(drawer);
  });

  // Escape reaches the <dialog> itself even when the component's own handler is not hit.
  drawer.querySelector('dialog')?.addEventListener('cancel', (event) => {
    event.preventDefault();
    drawer.closeDialog?.();
  });

  drawer.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    if (!event.target.closest('[data-booking-close]')) return;

    event.preventDefault();
    drawer.closeDialog?.();
  });

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;

    const trigger = event.target.closest('[data-booking-trigger]');
    if (!trigger) return;

    event.preventDefault();

    // The dialog element upgrades asynchronously, so fall back to showModal on a cold click.
    if (typeof drawer.showDialog === 'function') {
      drawer.showDialog();
    } else {
      customElements.whenDefined('dialog-component').then(() => drawer.showDialog());
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
